import { useLoyalty } from '../../../contexts/LoyaltyContext';

interface LoyaltyActivityFeedProps {
  maxItems?: number;
  showPoints?: boolean;
  showTimestamp?: boolean;
  title?: string;
}

const ACTIVITY_ICONS: Record<string, string> = {
  points_earned: '\u2B50',
  badge_unlocked: '\uD83C\uDFC5',
  tier_upgrade: '\uD83D\uDE80',
  challenge_complete: '\uD83C\uDFC6',
  referral: '\uD83D\uDC65',
  purchase: '\uD83D\uDED2',
  checkin: '\uD83D\uDCC5',
  mission: '\uD83C\uDFAF',
  reward_claimed: '\uD83C\uDF81',
};

function timeAgo(dateStr: string): string {
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function LoginCta() {
  return (
    <div className="loyalty-card p-6 text-center">
      <div className="text-3xl mb-2 opacity-50">&#128203;</div>
      <p className="text-[var(--color-text-muted)] text-sm">
        <a href="/login" className="text-[var(--color-primary)] hover:underline font-semibold">Sign in</a>
        {' '}to see your recent activity.
      </p>
    </div>
  );
}

export function LoyaltyActivityFeedBlock({ props }: { props: LoyaltyActivityFeedProps }) {
  const { data, loading } = useLoyalty();
  const maxItems = props.maxItems ?? 5;
  const showPoints = props.showPoints ?? true;
  const showTimestamp = props.showTimestamp ?? true;
  const title = props.title ?? 'Recent Activity';

  if (loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse space-y-4">
        <div className="h-5 w-36 bg-[var(--color-surface-border)] rounded" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[var(--color-surface-border)]" />
            <div className="flex-1">
              <div className="h-3 w-32 bg-[var(--color-surface-border)] rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const activities = data.recentActivity.slice(0, maxItems);

  if (activities.length === 0) {
    return (
      <div className="loyalty-card p-6 text-center">
        <p className="text-[var(--color-text-muted)] text-sm">
          No recent activity yet. Start earning points!
        </p>
      </div>
    );
  }

  return (
    <div className="loyalty-card p-6">
      <h2 className="loyalty-heading text-lg text-[var(--color-text)] mb-4">
        {title}
      </h2>
      <div className="space-y-3">
        {activities.map((activity, i) => (
          <div
            key={i}
            className="flex items-center gap-3 p-3 rounded-lg bg-[var(--color-surface)]/50 hover:bg-[var(--color-surface-border)]/30 transition-colors"
          >
            {/* Icon */}
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[var(--color-surface-border)]/50 text-lg shrink-0">
              {activity.icon
                ? (ACTIVITY_ICONS[activity.icon] ?? activity.icon)
                : (ACTIVITY_ICONS[activity.type] ?? '\u2728')}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[var(--color-text)] truncate">
                {activity.title}
              </p>
              {showTimestamp && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  {timeAgo(activity.occurredAt)}
                </p>
              )}
            </div>

            {/* Points */}
            {showPoints && activity.points !== 0 && (
              <span
                className={`text-sm font-bold whitespace-nowrap ${
                  activity.points > 0
                    ? 'text-green-500'
                    : 'text-red-400'
                }`}
              >
                {activity.points > 0 ? '+' : ''}{activity.points.toLocaleString()}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
